import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Navigation, Copy, Edit, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


export default function EventLocation({ eventId, isOwner }) {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    description: "",
    google_maps_url: ""
  });

  useEffect(() => {
    fetchLocation();
  }, [eventId]);

  const fetchLocation = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/location`, { withCredentials: true });
      setLocation(response.data);
      setFormData(response.data || { name: "", address: "", description: "", google_maps_url: "" });
    } catch (error) {
      console.error("Failed to fetch location:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/events/${eventId}/location`, formData, { withCredentials: true });
      setLocation(formData);
      setEditing(false);
      toast.success("Standort gespeichert! 📍");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(location?.address || "");
    toast.success("Adresse kopiert!");
  };

  const openInMaps = () => {
    const url = location?.google_maps_url || 
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location?.address || "")}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (editing || (!location?.address && isOwner)) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-green-500" />
          Standort bearbeiten
        </h3>
        <Input
          placeholder="Name des Ortes (z.B. Hotel Sonnenschein)"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="rounded-xl"
        />
        <Input
          placeholder="Adresse"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          className="rounded-xl"
        />
        <Textarea
          placeholder="Zusätzliche Infos (Parkmöglichkeiten, Anfahrt...)"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="rounded-xl"
        />
        <Input
          placeholder="Google Maps Link (optional)"
          value={formData.google_maps_url}
          onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
          className="rounded-xl"
        />
        <div className="flex gap-2">
          <Button onClick={handleSave} className="flex-1 rounded-xl bg-green-500 hover:bg-green-600">
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
          {location?.address && (
            <Button onClick={() => setEditing(false)} variant="outline" className="rounded-xl">
              Abbrechen
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!location?.address) {
    return (
      <div className="text-center py-12 text-gray-500">
        <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Noch kein Standort eingetragen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-green-600" />
            </div>
            <div>
              {location.name && <p className="font-semibold text-gray-900">{location.name}</p>}
              <p className="text-gray-600">{location.address}</p>
            </div>
          </div>
          {isOwner && (
            <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-gray-600">
              <Edit className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {location.description && (
          <p className="text-gray-600 mb-4 bg-gray-50 rounded-xl p-4">{location.description}</p>
        )}
        
        <div className="flex gap-2">
          <Button onClick={openInMaps} className="flex-1 rounded-xl bg-green-500 hover:bg-green-600">
            <Navigation className="w-4 h-4 mr-2" />
            Route anzeigen
          </Button>
          <Button onClick={copyAddress} variant="outline" className="rounded-xl">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
